import $ from 'jquery';

import { HEADER_CONTENT_TYPE } from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import {
  DEVICE_UNSUPPORTED,
  INVALID_SUBSCRIPTION_ERROR,
  PERMISSION_DENIED,
  PERMISSION_GRANTED,
  PERMISSION_PROMPT,
  READ,
} from 'constants/notifications';
import {
  PUSH_COMMENT_REPLIES,
  PUSH_VIDEO_COMMENTS,
  PUSH_VIDEO_PRIVACY_CHANGE,
  PUSH_VIDEO_READ,
  PUSH_VIDEO_RESHARE,
  PUSH_VIDEO_SHARE,
  PUSH_WEEKLY_DIGESTS,
} from 'constants/notificationSettings';
import { LOOM_URI } from 'constants/routes';
import { PUSH_SERVER_PUBLIC_KEY } from 'constants/runtimeConfig';
import * as analytics from 'utilities/analytics';
import { isPushable } from 'utilities/device';
import * as logger from 'utilities/loggerx';

import * as sentry from './sentry';

// register and install the service worker onto the client
const registerSW = async () => {
  // register service worker for push notifications
  if (isPushable) {
    // we need to scope it to the root directory sincen that way the sw
    // can listen to events sent down by the server to any route
    // /sw.js maps to assets/js/ServiceWorker.js, tnx kraken

    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } else {
    return Promise.resolve(false);
  }

  return Promise.resolve(true);
};

// subscribe user to 3rd party push service
const subscribePush = async () => {
  const registration = await navigator.serviceWorker.ready;
  const subscribeOptions = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUSH_SERVER_PUBLIC_KEY),
  };

  let pushSubscription = null;

  try {
    pushSubscription = await registration.pushManager.subscribe(
      subscribeOptions
    );
  } catch (err) {
    if (err.name === INVALID_SUBSCRIPTION_ERROR) {
      const curr = await registration.pushManager.getSubscription();

      await curr.unsubscribe();
      pushSubscription = await registration.pushManager.subscribe(
        subscribeOptions
      );
    } else {
      throw err;
    }
  }

  return pushSubscription;
};

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const sendSubscriptionToBackEnd = (subscription, activatedLocation) => {
  return fetch('/v1/users', {
    method: 'PATCH',
    credentials: 'include',
    headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
    body: JSON.stringify({
      push_subscription: subscription.toJSON(),
      activated_location: activatedLocation,
    }),
  }).then(response => {
    if (!response.ok) {
      throw new Error(
        `could not send notification subscription - ${response.status}`
      );
    }
  });
};

export const pushNotificationSettingsEnabled = notificationSettings => {
  return (
    notificationSettings[PUSH_VIDEO_READ] ||
    notificationSettings[PUSH_VIDEO_COMMENTS] ||
    notificationSettings[PUSH_COMMENT_REPLIES] ||
    notificationSettings[PUSH_WEEKLY_DIGESTS] ||
    notificationSettings[PUSH_VIDEO_SHARE] ||
    notificationSettings[PUSH_VIDEO_RESHARE] ||
    notificationSettings[PUSH_VIDEO_PRIVACY_CHANGE]
  );
};

/**
 * Asking for push permissions from user
 * Note on janky code: The permissions api was recently changed froma  callback
 * based request to a Promise based api. Since it's hard to know which one the
 * browser is using we account for both.
 * Refer: https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission
 */
export const askPermission = () => {
  return new Promise((resolve, reject) => {
    const permissionResult = Notification.requestPermission(result => {
      resolve(result);
    });

    if (permissionResult && permissionResult.then) {
      permissionResult.then(resolve).catch(reject);
    }
  }).then(permissionResult => {
    return permissionResult;
  });
};

export const getNotificationPermissionState = () => {
  if (!window.Notification) {
    return Promise.resolve(PERMISSION_DENIED);
  }

  if (navigator.permissions) {
    return navigator.permissions
      .query({ name: 'notifications' })
      .then(result => {
        return result.state;
      });
  }

  return new Promise(resolve => {
    resolve(Notification.permission);
  });
};

export const removeNonRootScopedServiceWorkers = async () => {
  if (isPushable) {
    const registrations = await navigator.serviceWorker.getRegistrations();

    registrations.forEach(registration => {
      if (registration.scope !== `${LOOM_URI}/`) {
        registration.unregister();
      }
    });
  }

  return;
};

export const registerNewPushSubscription = async (activatedLocation = '') => {
  try {
    const result = await registerSW();

    if (!result) {
      return;
    }

    const subscription = await subscribePush();

    await sendSubscriptionToBackEnd(subscription, activatedLocation);
  } catch (err) {
    logger.error(
      err,
      {
        message: `Unable to register push subscription from ${activatedLocation}`,
      },
      {
        team: logger.CREATE_TEAM,
      }
    );
  }
};

// initiates the client permissions+ServiceWorker setup for push notifs
export const setupPushNotifications = async activatedLocation => {
  try {
    if (!window.Notification) {
      return DEVICE_UNSUPPORTED;
    }

    let permission = await getNotificationPermissionState();

    if (permission === PERMISSION_PROMPT) {
      permission = await askPermission();
    }

    if (permission !== PERMISSION_GRANTED) {
      return PERMISSION_DENIED;
    }

    if (!isPushable) {
      return DEVICE_UNSUPPORTED;
    }

    await registerNewPushSubscription(activatedLocation);

    return PERMISSION_GRANTED;
  } catch (err) {
    sentry.logException(err, 'Error while setting up Push Notificatons');
  }
};

export const processPushEventData = data => {
  analytics.track(data.event, data);

  if (data.notif_id) {
    $.ajax({
      method: 'PATCH',
      url: `/v1/notifications/${data.notif_id}`,
      contentType: 'application/json',
      data: JSON.stringify({ status: READ }),
    });
  }
};

export const setDocumentTitle = (customText = null) => {
  if (!customText) {
    return;
  }

  document.title = customText;
};
